import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { TierConfigProvider } from "@/contexts/TierConfigContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { CompareProvider } from "@/contexts/CompareContext";
import { AppLayout } from "@/components/AppLayout";
import { CompareBar } from "@/components/company-search/CompareBar";

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Waitlist = lazy(() => import("./pages/Waitlist"));
const Profile = lazy(() => import("./pages/Profile"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Admin = lazy(() => import("./pages/Admin"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CICopilot = lazy(() => import("./pages/CICopilot"));
const SharedCopilotResponse = lazy(() => import("./pages/SharedCopilotResponse"));
const CompanyProfile = lazy(() => import("./pages/CompanyProfile"));
const CompanySearch = lazy(() => import("./pages/CompanySearch"));
const CompanyDetail = lazy(() => import("./pages/CompanyDetail"));
const CompanyCompare = lazy(() => import("./pages/CompanyCompare"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Terms = lazy(() => import("./pages/Terms"));
const CopyrightPolicy = lazy(() => import("./pages/CopyrightPolicy"));
const DMCAPolicy = lazy(() => import("./pages/DMCAPolicy"));
const QAChecklist = lazy(() => import("./pages/QAChecklist"));
const Help = lazy(() => import("./pages/Help"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TierConfigProvider>
        <CompareProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AppLayout>
                <Suspense fallback={null}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/waitlist" element={<Waitlist />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/ci-copilot" element={<CICopilot />} />
                    <Route path="/ci-copilot/shared/:shareId" element={<SharedCopilotResponse />} />
                    <Route path="/company/:companyName" element={<CompanyProfile />} />
                    <Route path="/companies" element={<CompanySearch />} />
                    <Route path="/companies/compare" element={<CompanyCompare />} />
                    <Route path="/companies/:companyId" element={<CompanyDetail />} />
                    <Route path="/invite/accept" element={<InviteAccept />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/copyright" element={<CopyrightPolicy />} />
                    <Route path="/dmca" element={<DMCAPolicy />} />
                    <Route path="/qa-checklist" element={<QAChecklist />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </AppLayout>
              <CompareBar />
            </BrowserRouter>
          </TooltipProvider>
        </CompareProvider>
      </TierConfigProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
